import {Injectable, OnDestroy} from '@angular/core';
import {isPayrollOperationPending} from 'app/services/operation-conflict';
import {Subscription} from 'rxjs/Subscription';
import {
  DealPayrollBreakdown, DEAL_PAYROLL_FORMULA_VERSION, hasExactDealPayrollMemoryMatch, isDealPayrollIdentityMatch
} from '../../../models/deal-payroll.model';
import {dealPayrollBlockerKeys} from '../../../models/deal-payroll-presentation';
import {
  ACCESS_DENIED_DETAIL_ERROR_KEY, EMBEDDED_DETAIL_SOURCE, FORBIDDEN_STATUS,
  GENERIC_DETAIL_ERROR_KEY, PRIMARY_BLOCKER_INDEX, REMOTE_DETAIL_SOURCE,
  SNAPSHOT_MISSING_CODE, SNAPSHOT_MISSING_DETAIL_ERROR_KEY
} from './deal-payroll-detail.loader.constants';
import {ReportService} from '../../../services/report.service';
import {DealPayrollDetailFormulaState} from './deal-payroll-detail.formula-state';
import {DealPayrollDetailLoaderContext, DealPayrollLoadError}
  from './deal-payroll-detail.loader.types';
import {
  DealPayrollDetailPresentationContext, DetailViewSource
} from './deal-payroll-detail.view.types';
import {DealPayrollDetailViewState} from './deal-payroll-detail.view-state';

const PAYROLL_PENDING_ERROR_KEY = 'PAYROLL_OPERATION_PENDING_HINT';

@Injectable()
export class DealPayrollDetailLoader implements OnDestroy {
  private requestToken: Object;
  private subscription: Subscription;

  constructor(
    private reportService: ReportService,
    public viewState: DealPayrollDetailViewState,
    private formulaState: DealPayrollDetailFormulaState
  ) {
  }

  loadInitial(context: DealPayrollDetailLoaderContext): void {
    const remembered = this.resolveRememberedBreakdown(context);
    if (remembered) {
      this.invalidate();
      this.viewState.useBreakdown(
        remembered, this.presentationContext(context, EMBEDDED_DETAIL_SOURCE));
      return;
    }
    this.load(context);
  }

  load(context: DealPayrollDetailLoaderContext): void {
    const token = {};
    this.requestToken = token;
    this.cancel();
    this.viewState.startLoading();
    this.formulaState.closeWithoutFocus();
    this.subscription = this.reportService.loadDealPayrollBreakdown(
      context.userId, context.month, context.refresh
    ).subscribe(
      (value: DealPayrollBreakdown) => this.applyResponse(value, context, token),
      (error: Object) => this.applyError(error, token)
    );
  }

  ngOnDestroy(): void {
    this.invalidate();
  }

  private applyResponse(
    value: DealPayrollBreakdown,
    context: DealPayrollDetailLoaderContext,
    token: Object
  ): void {
    if (token !== this.requestToken) {
      return;
    }
    if (!this.isValidResponse(value, context)) {
      this.viewState.rejectSnapshot(SNAPSHOT_MISSING_DETAIL_ERROR_KEY);
      return;
    }
    this.viewState.useBreakdown(
      value, this.presentationContext(context, REMOTE_DETAIL_SOURCE));
    this.reportService.rememberDealPayrollBreakdown(value);
    context.emitBreakdown(value);
  }

  private isValidResponse(
    value: DealPayrollBreakdown,
    context: DealPayrollDetailLoaderContext
  ): boolean {
    return isDealPayrollIdentityMatch(context.userId, context.month, value)
      && value.formulaVersion === DEAL_PAYROLL_FORMULA_VERSION
      && (context.refresh || hasExactDealPayrollMemoryMatch(context.reference, value));
  }

  private applyError(error: Object, token: Object): void {
    if (token !== this.requestToken) {
      return;
    }
    this.viewState.showError(this.resolveErrorKey(error));
  }

  private resolveErrorKey(error: Object): string {
    const failure = error as DealPayrollLoadError;
    if (isPayrollOperationPending(failure)) { return PAYROLL_PENDING_ERROR_KEY; }
    if (failure && failure.status === FORBIDDEN_STATUS) {
      return ACCESS_DENIED_DETAIL_ERROR_KEY;
    }
    const response = failure && failure.error;
    if (response && response.code === SNAPSHOT_MISSING_CODE) {
      return SNAPSHOT_MISSING_DETAIL_ERROR_KEY;
    }
    const blockerKeys = dealPayrollBlockerKeys(response && response.blockers);
    return blockerKeys.length ? blockerKeys[PRIMARY_BLOCKER_INDEX] : GENERIC_DETAIL_ERROR_KEY;
  }

  private resolveRememberedBreakdown(
    context: DealPayrollDetailLoaderContext
  ): DealPayrollBreakdown {
    const initial = context.reference;
    if (!isDealPayrollIdentityMatch(context.userId, context.month, initial)) {
      return undefined;
    }
    if (hasExactDealPayrollMemoryMatch(initial, initial)) {
      this.reportService.rememberDealPayrollBreakdown(initial);
      return initial;
    }
    return this.mergeRememberedTasks(initial, context);
  }

  private mergeRememberedTasks(
    initial: DealPayrollBreakdown,
    context: DealPayrollDetailLoaderContext
  ): DealPayrollBreakdown {
    const remembered = this.reportService.getDealPayrollBreakdown(
      context.userId, context.month);
    return hasExactDealPayrollMemoryMatch(initial, remembered)
      ? Object.assign({}, initial, {taskLines: remembered.taskLines.slice()})
      : undefined;
  }

  private presentationContext(
    context: DealPayrollDetailLoaderContext,
    source: DetailViewSource
  ): DealPayrollDetailPresentationContext {
    return {
      source: source,
      calculation: context.getCalculation(),
      calculationTrusted: context.isCalculationTrusted()
    };
  }

  private invalidate(): void {
    this.requestToken = {};
    this.cancel();
  }

  private cancel(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/deal-payroll-detail/deal-payroll-detail.loader.ts